function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as Y from 'yjs';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { log, removeObserver } from '@netless/forge-room';
import { CurveModel } from './renderable/CurveModel';
import { ElementModel } from './renderable/ElementModel';
import { SelectorModel } from './renderable/SelectorModel';
import { SegmentsModel } from './renderable/SegmentsModel';
import { LineModel } from './renderable/LineModel';
import { PointTextModel } from './renderable/PointTextModel';
import { TriangleModel } from './renderable/TriangleModel';
import { RectangleModel } from './renderable/RectangleModel';
import { backgroundElementsUndoOrigin, elementsUndoOrigin } from '../utils/constant';
import { EraserModel } from './renderable/EraserModel';
import { LaserPointerModel } from './renderable/LaserPointerModel';
import { WhiteboardPermissionFlag } from '../WhitePermissions';
import { StraightLineModel } from './renderable/StraightLineModel';
import { ImageModel } from './renderable/ImageModel';
export class RenderableModel extends EventEmitter {
  get uuid() {
    return uuidv4().replace(/-/g, '');
  }
  constructor(options) {
    super();
    _defineProperty(this, "scope", void 0);
    _defineProperty(this, "toolbarModel", void 0);
    _defineProperty(this, "userManager", void 0);
    _defineProperty(this, "elementModels", void 0);
    _defineProperty(this, "elements", void 0);
    _defineProperty(this, "layerId", void 0);
    _defineProperty(this, "maxIndex", -1);
    _defineProperty(this, "hasPermission", void 0);
    _defineProperty(this, "shadowEmitter", void 0);
    _defineProperty(this, "imageSets", void 0);
    _defineProperty(this, "liveCursor", void 0);
    _defineProperty(this, "isPerformanceMode", void 0);
    _defineProperty(this, "onElementsChange", event => {
      for (const [key, value] of event.changes.keys.entries()) {
        if (value.action === 'add') {
          const root = this.elements.get(key);
          if (root) {
            const model = this.convertToModel(root);
            if (model) {
              setTimeout(() => {
                this.emit('elementInsert', [model]);
              });
            }
            this.maxIndex = Math.max(this.maxIndex, model?.index ?? 0);
          }
        } else if (value.action === 'delete') {
          this.emit('elementRemove', key, this.layerId);
        }
      }
    });
    this.isPerformanceMode = options.isPerformanceMode;
    this.liveCursor = options.liveCursor;
    this.imageSets = options.imageSets;
    this.hasPermission = options.hasPermission;
    this.shadowEmitter = options.shadowEmitter;
    this.layerId = options.layerId;
    this.userManager = options.userManager;
    this.elements = options.elements;
    this.scope = options.scope;
    this.toolbarModel = options.toolbarModel;
    this.elementModels = new Map();
    for (const key of this.elements.keys()) {
      const type = this.elements.get(key)?.get('type');
      if (type === 'eraser' || type === 'laser') {
        this.elements.delete(key);
      }
    }
    this.flushRenderables();
    this.elements.observe(this.onElementsChange);
  }
  flushRenderables() {
    this.emit('elementClear');
    const elements = Array.from(this.elements.values()).map(v => this.convertToModel(v)).filter(v => !!v);
    elements.sort((a, b) => this.compareElements(a, b));
    this.maxIndex = elements.reduce((r, n) => Math.max(r, n.index), -1);
    const clearIds = [];
    elements.forEach(model => {
      if (model.shadow !== '') {
        clearIds.push(model.uuid);
      }
    });
    setTimeout(() => {
      this.emit('elementInsert', elements);
      clearIds.forEach(id => {
        this.elements.get(id)?.set(ElementModel.KEYS.shadow, '');
      });
    });
  }
  convertToModel(yMap) {
    const type = yMap.get('type');
    let model = null;
    const uuid = yMap.get(ElementModel.KEYS.uuid);
    if (uuid) {
      const existingModel = this.elementModels.get(uuid);
      if (existingModel) {
        existingModel.shadowEmitter = this.shadowEmitter;
        existingModel.bindObserver();
        return existingModel;
      }
    }
    if (type === 'curve') {
      model = new CurveModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (['ellipse'].indexOf(type) >= 0) {
      model = new SegmentsModel(yMap, this.scope, type, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'selector') {
      model = new SelectorModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'arrow') {
      model = new LineModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'line') {
      model = new StraightLineModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'point-text') {
      model = new PointTextModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'triangle') {
      model = new TriangleModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'rectangle') {
      model = new RectangleModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'eraser') {
      model = new EraserModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    } else if (type === 'laser') {
      model = new LaserPointerModel(this.userManager.selfId, yMap, this.scope, this.liveCursor, uuid => {
        this.removeElementItem(uuid);
      }, this.isPerformanceMode);
    } else if (type === 'image') {
      model = new ImageModel(yMap, this.scope, this.imageSets, this.liveCursor, this.isPerformanceMode);
    }
    if (model) {
      model.shadowEmitter = this.shadowEmitter;
      model.bindObserver();
      this.elementModels.set(model.uuid, model);
    }
    return model;
  }
  initElement(element) {
    element.shadowEmitter = this.shadowEmitter;
  }
  compareElementRole(roleA, roleB) {
    const a = roleA === 'background' ? 0 : 1;
    const b = roleB === 'background' ? 0 : 1;
    return a - b;
  }
  compareElements(a, b) {
    const roleCompare = this.compareElementRole(a.role, b.role);
    if (roleCompare !== 0) {
      return roleCompare;
    }
    if (a.index !== b.index) {
      return a.index - b.index;
    }
    return a.uuid.localeCompare(b.uuid);
  }
  isBackgroundElement(uuid) {
    const element = this.elements.get(uuid);
    if (!element) {
      return false;
    }
    return (element.get('role') ?? 'normal') === 'background';
  }
  removeElementItem(uuid) {
    if (this.isBackgroundElement(uuid)) {
      return;
    }
    this.elements.delete(uuid);
  }
  confirmPermission() {
    const hasPermission = this.hasPermission(WhiteboardPermissionFlag.draw);
    if (!hasPermission) {
      log('[@netless/forge-whiteboard] no permission to draw', {}, 'warn');
    }
    return hasPermission;
  }
  createImage(src) {
    let fit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'legacy';
    let role = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'normal';
    if (!this.confirmPermission()) {
      return;
    }
    if (role === 'background') {
      return this.upsertBackgroundImage(src, fit);
    }
    const yMap = new Y.Map();
    this.elements.doc?.transact(() => {
      const uuid = this.uuid;
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'image');
      yMap.set('role', role);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    const model = new ImageModel(yMap, this.scope, this.imageSets, this.liveCursor, this.isPerformanceMode);
    model.bindObserver();
    model.root.set('src', src);
    model.ownerId = this.userManager.selfId;
    this.fitImageToViewport(src, model, fit);
  }
  upsertBackgroundImage(src, fit) {
    const backgroundEntries = Array.from(this.elements.entries()).filter(_ref => {
      let [, elementMap] = _ref;
      return elementMap.get('type') === 'image' && (elementMap.get('role') ?? 'normal') === 'background';
    });
    let targetMap = backgroundEntries[0]?.[1] ?? null;
    if (this.elements.doc) {
      this.elements.doc.transact(() => {
        backgroundEntries.slice(1).forEach(_ref2 => {
          let [key] = _ref2;
          return this.elements.delete(key);
        });
        if (!targetMap) {
          targetMap = new Y.Map();
          const uuid = this.uuid;
          targetMap.set(ElementModel.KEYS.index, -1);
          targetMap.set(ElementModel.KEYS.uuid, uuid);
          targetMap.set('type', 'image');
          targetMap.set('role', 'background');
          targetMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
          this.elements.set(uuid, targetMap);
        } else {
          targetMap.set('role', 'background');
          targetMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
        }
      }, backgroundElementsUndoOrigin);
    }
    if (!targetMap) {
      return;
    }
    const model = this.convertToModel(targetMap);
    if (!model || !(model instanceof ImageModel)) {
      return;
    }
    model.root.set('src', src);
    model.ownerId = this.userManager.selfId;
    this.fitImageToViewport(src, model, fit);
  }
  fitImageToViewport(src, model, fit) {
    const center = this.scope.project.view.center;
    const fallbackMatrix = new this.scope.Matrix();
    fallbackMatrix.translate({
      x: center.x,
      y: center.y
    });
    this.setElementPointsMatrix(model, fallbackMatrix);
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      if (naturalWidth <= 0 || naturalHeight <= 0) {
        return;
      }
      const viewportBounds = this.scope.project.view.bounds;
      const maxWidth = fit === 'contain' ? viewportBounds.width : viewportBounds.width * 2 / 3;
      const maxHeight = fit === 'contain' ? viewportBounds.height : viewportBounds.height * 2 / 3;
      const fitScale = fit === 'contain' ? Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight) : Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
      const nextMatrix = new this.scope.Matrix();
      nextMatrix.translate({
        x: center.x,
        y: center.y
      });
      if (fitScale > 0 && Number.isFinite(fitScale)) {
        nextMatrix.scale(fitScale);
      }
      this.setElementPointsMatrix(model, nextMatrix);
    };
    img.onerror = () => {
      log('[@netless/forge-whiteboard] failed to preload image for viewport fitting', {
        src
      }, 'warn');
    };
    img.src = src;
  }
  setElementPointsMatrix(model, matrix) {
    const values = [matrix.a, matrix.b, matrix.c, matrix.d, matrix.tx, matrix.ty];
    if (model.root.doc) {
      model.root.doc.transact(() => {
        model.root.set(ElementModel.KEYS.pointsMatrix, values);
      }, model.isBackground ? backgroundElementsUndoOrigin : elementsUndoOrigin);
    } else {
      model.root.set(ElementModel.KEYS.pointsMatrix, values);
    }
  }
  createCurve() {
    let shadow = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const curveModel = new CurveModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, curveModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'curve');
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    curveModel.bindObserver();
    this.initElement(curveModel);
    return curveModel;
  }
  createLaserPointer() {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const model = new LaserPointerModel(this.userManager.selfId, yMap, this.scope, this.liveCursor, uuid => {
      this.removeElementItem(uuid);
    }, this.isPerformanceMode);
    this.elementModels.set(uuid, model);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'laser');
      yMap.set(ElementModel.KEYS.points, new Y.Array());
      yMap.set('removed', false);
      yMap.set(ElementModel.KEYS.shadow, 'layer');
      yMap.set(ElementModel.KEYS.strokeWidth, 8);
      yMap.set(ElementModel.KEYS.strokeColor, '#F44336');
      yMap.set(ElementModel.KEYS.fillColor, '#F44336');
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    model.bindObserver();
    this.initElement(model);
    return model;
  }
  createEraser() {
    if (!this.hasPermission(WhiteboardPermissionFlag.deleteSelf) && !this.hasPermission(WhiteboardPermissionFlag.deleteOthers)) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const model = new EraserModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, model);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'eraser');
      yMap.set(ElementModel.KEYS.points, new Y.Array());
      yMap.set(ElementModel.KEYS.shadow, 'layer');
      yMap.set(ElementModel.KEYS.strokeWidth, 4);
      yMap.set(ElementModel.KEYS.strokeColor, '#9E9E9E');
      yMap.set(ElementModel.KEYS.fillColor, '#9E9E9E');
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    model.bindObserver();
    return model;
  }
  createTriangle(shadow) {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const triangleModel = new TriangleModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, triangleModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'triangle');
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      yMap.set('points', new Y.Array());
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    triangleModel.bindObserver();
    this.initElement(triangleModel);
    triangleModel.dashArray = this.toolbarModel.dashArray;
    return triangleModel;
  }
  createRectangle(shadow) {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const rect = new RectangleModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, rect);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'rectangle');
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      const initPoints = new Y.Array();
      initPoints.push([0, 0, 0, 0]);
      yMap.set('points', initPoints);
      yMap.set('radius', 0);
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    rect.bindObserver();
    this.initElement(rect);
    rect.dashArray = this.toolbarModel.dashArray;
    return rect;
  }
  createSegmentedPath(type, shadow) {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const segmentsModel = new SegmentsModel(yMap, this.scope, type, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, segmentsModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', type);
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      yMap.set(ElementModel.KEYS.points, new Y.Array());
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    segmentsModel.bindObserver();
    this.initElement(segmentsModel);
    segmentsModel.dashArray = this.toolbarModel.dashArray;
    return segmentsModel;
  }
  createSelector() {
    let _shadow = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (!this.hasPermission(WhiteboardPermissionFlag.editSelf) && !this.hasPermission(WhiteboardPermissionFlag.deleteOthers)) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const selectorModel = new SelectorModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, selectorModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'selector');
      const initPoints = new Y.Array();
      initPoints.push([0, 0, 0, 0]);
      yMap.set(ElementModel.KEYS.points, initPoints);
      yMap.set(ElementModel.KEYS.shadow, 'layer');
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    selectorModel.bindObserver();
    selectorModel.shadow = 'layer';
    return selectorModel;
  }
  createStraightLine(shadow) {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const straightLineModel = new StraightLineModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, straightLineModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'line');
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      yMap.set('points', new Y.Array());
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    straightLineModel.bindObserver();
    this.initElement(straightLineModel);
    straightLineModel.dashArray = this.toolbarModel.dashArray;
    return straightLineModel;
  }
  createLinePath(shadow) {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const lineModel = new LineModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, lineModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'arrow');
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      yMap.set('headArrow', 'none');
      yMap.set('tailArrow', 'normal');
      yMap.set(ElementModel.KEYS.points, new Y.Array());
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    lineModel.bindObserver();
    this.initElement(lineModel);
    lineModel.dashArray = this.toolbarModel.dashArray;
    return lineModel;
  }
  createPointText(x, y, shadow) {
    if (!this.confirmPermission()) {
      return null;
    }
    const yMap = new Y.Map();
    const uuid = this.uuid;
    const pointTextModel = new PointTextModel(yMap, this.scope, this.liveCursor, this.isPerformanceMode);
    this.elementModels.set(uuid, pointTextModel);
    this.elements.doc?.transact(() => {
      yMap.set(ElementModel.KEYS.index, ++this.maxIndex);
      yMap.set(ElementModel.KEYS.uuid, uuid);
      yMap.set('type', 'point-text');
      if (shadow) {
        yMap.set(ElementModel.KEYS.shadow, 'layer');
      }
      yMap.set(ElementModel.KEYS.points, Y.Array.from([x, y]));
      yMap.set(ElementModel.KEYS.strokeWidth, this.toolbarModel.strokeWidth);
      yMap.set(ElementModel.KEYS.strokeColor, this.toolbarModel.strokeColor);
      yMap.set(ElementModel.KEYS.fillColor, this.toolbarModel.fillColor);
      yMap.set(ElementModel.KEYS.ownerId, this.userManager.selfId);
      this.elements.set(uuid, yMap);
    }, elementsUndoOrigin);
    pointTextModel.bindObserver();
    pointTextModel.fontSize = this.toolbarModel.fontSize;
    pointTextModel.fontFamily = this.toolbarModel.fontFamily;
    this.initElement(pointTextModel);
    return pointTextModel;
  }
  clearElement() {
    this.elements.doc.transact(() => {
      const removeIds = [];
      for (const key of this.elements.keys()) {
        const elementMap = this.elements.get(key);
        if (elementMap) {
          const type = elementMap.get('type');
          if (['eraser', 'selector'].indexOf(type) >= 0) {
            removeIds.push(key);
          }
        }
      }
      removeIds.forEach(id => this.elements.delete(id));
    });
  }
  dispose(clearElements) {
    removeObserver(this.elements, this.onElementsChange);
    if (clearElements) {
      this.elements.clear();
    }
    Array.from(this.elementModels.values()).forEach(model => {
      model.dispose();
      model.disposeObserver();
    });
  }
}